/**
 * Deterministic RNG.
 *
 * The seed lives in GameState and advances with every roll, so a battle or a
 * comet outcome replays identically from a save file — which is also what makes
 * every random system in this engine unit-testable with golden values, and what
 * will let the future server validate client-reported outcomes.
 */

/** One mulberry32 step: returns the next float in [0,1) and the advanced seed. */
export function nextRandom(seed: number): { value: number; seed: number } {
  let a = (seed + 0x6d2b79f5) >>> 0;
  let t = a;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return { value: ((t ^ (t >>> 14)) >>> 0) / 4294967296, seed: a };
}

/** Integer in [min, max] inclusive. */
export function nextInt(
  seed: number,
  min: number,
  max: number,
): { value: number; seed: number } {
  const r = nextRandom(seed);
  return { value: min + Math.floor(r.value * (max - min + 1)), seed: r.seed };
}

/** A stateful wrapper for code paths that roll many times (combat). */
export function createRng(seed: number): { random(): number; int(min: number, max: number): number; seed(): number } {
  let s = seed;
  return {
    random() {
      const r = nextRandom(s);
      s = r.seed;
      return r.value;
    },
    int(min: number, max: number) {
      const r = nextInt(s, min, max);
      s = r.seed;
      return r.value;
    },
    seed() {
      return s;
    },
  };
}
